'use client';

/**
 * Progress charts.
 *
 * Design decisions worth keeping:
 *   • ONE measure per chart. Speed and practice time are different scales, so
 *     they get two charts rather than one dual-axis chart.
 *   • Single series, so there is no legend box — the title names the measure.
 *   • Every bar carries a visible value label. That is not decoration: the
 *     light-mode series colour sits just under 3:1 against a white card, and a
 *     visible label is the required relief.
 *   • A table view is available for every chart, so the data is reachable
 *     without reading colour or shape at all.
 *
 * Colours come from --chart-* tokens (see styles/kids.css) so light and dark
 * each use their own validated step rather than one flipped hue.
 */
import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Table2, LineChart as LineIcon } from 'lucide-react';
import type { DayPoint } from '../lib/analytics';

/* ────────────────────────────────────────────────────────────── chrome ──── */

const AXIS = { fontSize: 11, fontWeight: 700, fill: 'var(--chart-axis)' } as const;

function ChartFrame({
  title,
  hint,
  children,
  table,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  table: React.ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  return (
    <section className="k-card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base">{title}</h3>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="k-btn k-btn-ghost k-btn-sm shrink-0"
          aria-pressed={showTable}
        >
          {showTable ? <LineIcon size={15} /> : <Table2 size={15} />}
          <span className="sr-only sm:not-sr-only">{showTable ? 'Chart' : 'Table'}</span>
        </button>
      </div>
      {showTable ? <div className="overflow-x-auto">{table}</div> : children}
    </section>
  );
}

function Tip({
  active,
  payload,
  label,
  unit,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: string | number;
  unit: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2 rounded-xl text-sm font-extrabold"
      style={{
        background: 'var(--card)',
        border: '2px solid var(--border-strong)',
        boxShadow: 'var(--k-shadow)',
        color: 'var(--foreground)',
      }}
    >
      <div className="text-xs font-bold text-muted-foreground">{label}</div>
      <div className="tabular-nums">
        {payload[0].value} {unit}
      </div>
    </div>
  );
}

function DataTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} className="k-eyebrow text-left py-2 pr-4">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
            {row.map((cell, j) => (
              <td key={j} className="py-1.5 pr-4 font-bold tabular-nums">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─────────────────────────────────────────────────────────── speed line ─── */

export interface SpeedPoint {
  index: number;
  wpm: number;
  accuracy: number;
  label: string;
}

export function SpeedChart({ data }: { data: SpeedPoint[] }) {
  if (data.length < 2) {
    return (
      <section className="k-card p-4">
        <h3 className="text-base">Speed over time</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Finish two lessons and your speed line appears here.
        </p>
      </section>
    );
  }

  const last = data[data.length - 1];

  return (
    <ChartFrame
      title="Speed over time"
      hint="Words per minute, one point per lesson"
      table={
        <DataTable
          head={['Lesson', 'Date', 'Speed (wpm)', 'Accuracy (%)']}
          rows={data.map((d) => [d.index, d.label, d.wpm, d.accuracy])}
        />
      }
    >
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 18, right: 30, bottom: 4, left: -18 }}>
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 4" vertical={false} />
            <XAxis
              dataKey="index"
              tick={AXIS}
              tickLine={false}
              axisLine={{ stroke: 'var(--chart-grid)' }}
            />
            <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} allowDecimals={false} />
            <Tooltip
              content={<Tip unit="wpm" />}
              cursor={{ stroke: 'var(--chart-grid)', strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="wpm"
              stroke="var(--chart-speed)"
              strokeWidth={2}
              dot={{ r: 4, fill: 'var(--chart-speed)', stroke: 'var(--card)', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Selective direct label — the latest value, never a number on every point. */}
      <p className="text-sm font-extrabold mt-1" style={{ color: 'var(--chart-speed)' }}>
        Latest: {last.wpm} wpm
        <span className="text-muted-foreground font-bold"> · {last.label}</span>
      </p>
    </ChartFrame>
  );
}

/* ───────────────────────────────────────────────────────── practice bars ── */

export function PracticeChart({ data }: { data: DayPoint[] }) {
  const total = data.reduce((sum, d) => sum + d.minutes, 0);

  if (total === 0) {
    return (
      <section className="k-card p-4">
        <h3 className="text-base">Practice minutes</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Your daily practice will show up here once you finish a lesson.
        </p>
      </section>
    );
  }

  return (
    <ChartFrame
      title="Practice minutes"
      hint={`${total} minutes over the last ${data.length} days`}
      table={
        <DataTable
          head={['Day', 'Minutes', 'Lessons', 'Speed (wpm)']}
          rows={data.map((d) => [d.label, d.minutes, d.sessions, d.wpm || '—'])}
        />
      }
    >
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 18, right: 8, bottom: 4, left: -18 }}
            barCategoryGap={2}
          >
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 4" vertical={false} />
            <XAxis
              dataKey="label"
              tick={AXIS}
              tickLine={false}
              axisLine={{ stroke: 'var(--chart-grid)' }}
              interval="preserveStartEnd"
              minTickGap={18}
            />
            <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} allowDecimals={false} />
            <Tooltip
              content={<Tip unit="minutes" />}
              cursor={{ fill: 'var(--chart-grid)', opacity: 0.4 }}
            />
            <Bar
              dataKey="minutes"
              fill="var(--chart-practice)"
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}

/* ───────────────────────────────────────────────────── weekly streak strip ─ */

export function WeekStrip({ days }: { days: DayPoint[] }) {
  const week = days.slice(-7);
  return (
    <div className="flex items-end justify-between gap-1.5">
      {week.map((day) => {
        const active = day.sessions > 0;
        return (
          <div key={day.day} className="flex-1 text-center">
            <div
              className="mx-auto rounded-xl grid place-items-center"
              style={{
                height: 44,
                background: active ? 'var(--k-mint)' : 'var(--muted)',
                color: active ? '#fff' : 'var(--faint)',
                border: `2px solid ${active ? 'var(--k-mint)' : 'var(--border)'}`,
                fontSize: 18,
              }}
              aria-hidden="true"
            >
              {active ? '🔥' : '·'}
            </div>
            <div className="text-[0.62rem] font-extrabold text-muted-foreground mt-1 uppercase">
              {day.label.split(' ')[0]}
            </div>
            <span className="sr-only">
              {day.label}: {active ? `${day.minutes} minutes practised` : 'no practice'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
